import { generatePath } from 'react-router-dom'

import routes from '@src/Routes/routes'
import { Feature } from '@type/features'

// Pure paywall mapping, split out of useGetHideWithPaywall so that modules
// with no React or query dependency (nav filters, landing routes, the
// feature-access helpers) can resolve a paywall path without importing
// @queries/Billing and the module cycle it drags in under vitest.

export enum FeaturesPaywall {
  Contracts = 'contracts',
  CostCenters = 'cost-centers',
  Integrations = 'integrations',
  AccountsPayable = 'accounts-payable',
  AccountsReceivable = 'accounts-receivable',
  Xero = 'xero',
  Quickbooks = 'quickbooks',
  Softledger = 'softledger',
  Netsuite = 'netsuite',
  Bamboo = 'bamboo',
  GoogleCalendar = 'google-calendar',
  AI = 'ai',
  API = 'api',
  Mailbox = 'mailbox',
}

export const FeaturesToPaywall: Partial<Record<Feature, FeaturesPaywall>> = {
  [Feature.integrations]: FeaturesPaywall.Integrations,
  [Feature.accountsPayable]: FeaturesPaywall.AccountsPayable,
  [Feature.accountsReceivable]: FeaturesPaywall.AccountsReceivable,
  [Feature.accountingIntegrations]: FeaturesPaywall.Integrations,
  [Feature.netsuite]: FeaturesPaywall.Netsuite,
  [Feature.costCenters]: FeaturesPaywall.CostCenters,
  [Feature.googleCalendar]: FeaturesPaywall.GoogleCalendar,
  [Feature.bambooHR]: FeaturesPaywall.Bamboo,
  [Feature.api]: FeaturesPaywall.API,
  [Feature.mailbox]: FeaturesPaywall.Mailbox,
  [Feature.contractManagement]: FeaturesPaywall.Contracts,
  [Feature.zapier]: FeaturesPaywall.Integrations,
}

// Integrations are available on every plan, free included: connecting Xero,
// NetSuite, Zapier, BambooHR or Google Calendar and wiring one into a workflow
// is not a paid capability. The `billing_plan` rows still withhold these
// entitlements and `plan_features` still reports them false, so the unlock is
// applied where plan_features is read rather than per call site — some gates
// are driven by a feature name the BACKEND puts on a workflow template
// (`template.options.feature`), which no frontend call site can opt out of.
// No backend endpoint has ever enforced these; integration APIs gate on the
// integration.view / integration.manage permissions, which are unaffected.
// To re-gate one of these, delete it from this set.
export const UNGATED_FEATURES: ReadonlySet<Feature> = new Set([
  Feature.integrations,
  Feature.accountingIntegrations,
  Feature.netsuite,
  Feature.googleCalendar,
  Feature.bambooHR,
  Feature.zapier,
])

export const getPaywallPath = (paywall: FeaturesPaywall) => {
  return generatePath(routes.private.paywall, { feature: paywall })
}
